import React from "react";
import JobPagination from "../JobPagination";
import { useTranslation } from "../../hooks/useTranslation";

/* Tabela detalhada (extrato) com ordenação server-side e paginação no padrão
   do sistema (JobPagination). Colunas conforme spec/mockup; a coluna de custo
   só aparece com showCost=true (flag server-driven, oculta nesta sprint). */
const SortArrow = ({ direction }) => (
  <span className="arrow">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d={direction === "asc" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
    </svg>
  </span>
);

const IntegrationBadge = () => (
  <span className="badge-int">
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
    </svg>
    API
  </span>
);

const PRODUCT_COLORS = {
  PE_CUSTOM: "#D84E2B",
  DC_CONTESTACAO: "#643184",
  DC_CONTESTACAO_DOSSIE: "#1976d2",
  DC_PROCON: "#0d9488",
};

function hexA(hex, alpha) {
  const n = parseInt(hex.slice(1), 16);
  /* eslint-disable no-bitwise */
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
  /* eslint-enable no-bitwise */
}

const ConsumptionDetailsTable = ({
  rows = [],
  pagination = null,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onPageSizeChange,
  showCost = false,
  loading = false,
}) => {
  const { t, formatDateTime, formatNumber } = useTranslation("consumption");

  const fmtInt = (v) =>
    v == null ? "—" : formatNumber ? formatNumber(v) : String(v);
  const fmtMoney = (v) =>
    v == null
      ? "—"
      : `R$ ${Number(v).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
  const fmtDT = (v) => (v ? formatDateTime(v, { emptyValue: "—" }) : "—");

  const columns = [
    { key: "job_id", label: t("colJobId") },
    { key: "insertion_timestamp", label: t("colCreated") },
    { key: "process_timestamp", label: t("colProcessed") },
    { key: "product", label: t("colServiceProduct") },
    { key: "job_name", label: t("colName") },
    { key: "company", label: t("colCompany") },
    { key: "tenant", label: t("colTenant") },
    { key: "cell", label: t("colCell") },
    { key: "user", label: t("colUser") },
    { key: "external_id", label: t("colExternalId") },
    { key: "mode", label: t("colMode"), align: "center" },
    { key: "pages", label: t("colPages"), num: true },
    ...(showCost ? [{ key: "cost", label: t("colCost"), num: true }] : []),
  ];

  return (
    <section className="card details-card">
      <div className="card-head">
        <h2>
          <span className="ico">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </span>
          {t("detailTitle")}
        </h2>
        <span className="muted">
          {pagination
            ? t("recordsInPeriod", { count: fmtInt(pagination.total_count) })
            : ""}
        </span>
      </div>
      <div className="table-scroll">
        <table className="dtable">
          <thead>
            <tr>
              {columns.map((col) => {
                const sorted = sortBy === col.key;
                return (
                  <th
                    key={col.key}
                    className={`${col.num ? "num" : ""} ${
                      col.align === "center" ? "center" : ""
                    } ${sorted ? "sorted" : ""}`}
                    onClick={() => onSort && onSort(col.key)}
                  >
                    <span className="sort">
                      {col.label}
                      <SortArrow direction={sorted ? sortOrder : "desc"} />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="dtable-empty">
                  {t("loading")}
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="dtable-empty">
                  {t("noData")}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const color = PRODUCT_COLORS[row.product_code] || "#643184";
                return (
                  <tr key={row.job_id}>
                    <td>
                      <span className="idcell">#{row.job_id}</span>
                    </td>
                    <td>{fmtDT(row.insertion_timestamp)}</td>
                    <td>{fmtDT(row.process_timestamp)}</td>
                    <td>
                      <span
                        className="svc-chip"
                        style={{ background: hexA(color, 0.12), color }}
                        title={`${row.service_label} → ${row.product_label}`}
                      >
                        <span className="sw" style={{ background: color }} />
                        {row.product_label}
                      </span>
                    </td>
                    <td className="cell-name" title={row.job_name || ""}>
                      <span className="jobname">{row.job_name || "—"}</span>
                    </td>
                    <td>{row.company || "—"}</td>
                    <td>{row.tenant || "—"}</td>
                    <td>{row.cell || "—"}</td>
                    <td>
                      <span className="usercell">
                        {row.is_integration && <IntegrationBadge />}
                        {row.user_name || row.api_key_name || "—"}
                        {row.user_inactive && (
                          <span className="tag-inactive">{t("inactive")}</span>
                        )}
                      </span>
                    </td>
                    <td>{row.external_id || <span className="muted">—</span>}</td>
                    <td className="center">
                      {row.mode ? (
                        <span className={`mode-chip mode-${row.mode.toLowerCase()}`}>
                          {row.mode}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">{fmtInt(row.pages)}</td>
                    {showCost && <td className="num cost">{fmtMoney(row.cost)}</td>}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {pagination && pagination.total_count > 0 && (
        <JobPagination
          page={pagination.page}
          pageSize={pagination.page_size}
          total={pagination.total_count}
          totalPages={pagination.total_pages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      )}
    </section>
  );
};

export default ConsumptionDetailsTable;
